import { STANDARD_DASHBOARD_TEMPLATE_ID } from './dashboardTemplates';

export const parseWidgetConfig = (widget) => {
  if (!widget?.widget_config_json) {
    return null;
  }
  try {
    return JSON.parse(widget.widget_config_json);
  } catch {
    return null;
  }
};

export const normalizeWidget = (widget) => ({
  ...widget,
  x_position: widget.x_position ?? widget.xPosition ?? 0,
  y_position: widget.y_position ?? widget.yPosition ?? 0,
  widget_type: widget.widget_type ?? widget.widgetType ?? 0,
  widget_name: widget.widget_name ?? widget.widgetName ?? '',
  widget_config_json: widget.widget_config_json ?? widget.widgetConfigJson ?? '{}',
  display_order: widget.display_order ?? widget.displayOrder ?? 0,
});

export const getNextYPosition = (widgets) => {
  if (!widgets?.length) {
    return 0;
  }
  return Math.max(...widgets.map((w) => w.y_position ?? 0)) + 1;
};

const parseKpiSlotKeyIndex = (slotKey) => {
  const match = String(slotKey || '').match(/^kpi_(\d+)$/);
  return match ? Number(match[1]) : null;
};

const isSameDashboardSlot = (widget, slotConfig, yPosition) => {
  const config = parseWidgetConfig(widget);
  if (!config || !slotConfig) {
    return false;
  }

  const sameY = yPosition == null
    || (widget.y_position ?? 0) === yPosition;

  if (!sameY) {
    return false;
  }

  if (slotConfig.row_id && config.row_id && config.row_id !== slotConfig.row_id) {
    return false;
  }

  const sameSlotKey = slotConfig.slot_key != null
    && config.slot_key === slotConfig.slot_key;
  const sameSlotIndex = slotConfig.slot_index != null
    && config.slot_index === slotConfig.slot_index;

  return sameSlotKey || sameSlotIndex;
};

/** Match placeholder slot when attaching a report (id, slot_key + row, or slot_index + row). */
export const resolveDashboardTargetWidget = (
  dashboardWidgets,
  { targetWidgetId, slotKey, slotIndex, yPosition, rowId } = {},
) => {
  const widgets = (dashboardWidgets || []).map(normalizeWidget);
  const normalizedTargetId = targetWidgetId != null ? String(targetWidgetId) : null;

  if (normalizedTargetId) {
    const byId = widgets.find((widget) => String(widget.id) === normalizedTargetId);
    if (byId) {
      return byId;
    }
  }

  const scopeWidgets = (list) => {
    let scoped = list;
    if (yPosition != null) {
      scoped = scoped.filter((widget) => (widget.y_position ?? 0) === yPosition);
    }
    if (rowId) {
      scoped = scoped.filter((widget) => parseWidgetConfig(widget)?.row_id === rowId);
    }
    return scoped;
  };

  const slotDescriptor = {
    slot_key: slotKey || null,
    slot_index: Number.isFinite(slotIndex) ? slotIndex : parseKpiSlotKeyIndex(slotKey),
    row_id: rowId || null,
  };

  if (slotKey) {
    const matches = scopeWidgets(
      widgets.filter((widget) => parseWidgetConfig(widget)?.slot_key === slotKey),
    );
    if (matches.length === 1) {
      return matches[0];
    }
    if (matches.length > 1) {
      return null;
    }
  }

  if (Number.isFinite(slotDescriptor.slot_index)) {
    const indexMatches = scopeWidgets(
      widgets.filter((widget) => {
        const config = parseWidgetConfig(widget);
        return config?.slot_index === slotDescriptor.slot_index
          && (
            config?.slot_type === 'kpi'
            || String(config?.slot_key || '').startsWith('kpi')
            || slotKey == null
          );
      }),
    );
    if (indexMatches.length === 1) {
      return indexMatches[0];
    }
    if (indexMatches.length > 1) {
      return null;
    }

    const genericIndexMatches = scopeWidgets(
      widgets.filter(
        (widget) => parseWidgetConfig(widget)?.slot_index === slotDescriptor.slot_index,
      ),
    );
    if (genericIndexMatches.length === 1) {
      return genericIndexMatches[0];
    }
  }

  if (slotKey || Number.isFinite(slotDescriptor.slot_index)) {
    const looseMatches = scopeWidgets(
      widgets.filter((widget) => isSameDashboardSlot(widget, slotDescriptor, yPosition)),
    );
    if (looseMatches.length === 1) {
      return looseMatches[0];
    }
  }

  return null;
};

export const upsertDashboardWidget = (widgets, updatedWidget) => {
  const normalized = normalizeWidget(updatedWidget);
  const widgetId = String(normalized.id);
  const updatedConfig = parseWidgetConfig(normalized);
  const list = (widgets || []).map(normalizeWidget);
  const exists = list.some((widget) => String(widget.id) === widgetId);

  if (exists) {
    return list.map((widget) => (
      String(widget.id) === widgetId ? normalized : widget
    ));
  }

  let next = [...list];
  if (updatedConfig?.report_id && updatedConfig?.slot_key != null) {
    const updatedY = normalized.y_position ?? 0;
    next = next.filter((widget) => {
      if (String(widget.id) === widgetId) {
        return true;
      }
      const config = parseWidgetConfig(widget);
      const sameRow = (widget.y_position ?? 0) === updatedY;
      const sameSlot = sameRow
        && config?.slot_key === updatedConfig.slot_key
        && (
          updatedConfig.slot_index == null
          || config?.slot_index === updatedConfig.slot_index
        );
      return !(sameSlot && !config?.report_id);
    });
  }

  return [...next, normalized];
};

export const groupWidgetsByRow = (widgets) => {
  const normalized = (widgets || []).map(normalizeWidget);
  const byY = {};

  normalized.forEach((widget) => {
    const y = widget.y_position ?? 0;
    if (!byY[y]) {
      byY[y] = [];
    }
    byY[y].push(widget);
  });

  return Object.keys(byY)
    .map(Number)
    .sort((a, b) => a - b)
    .map((y) => ({
      yPosition: y,
      widgets: byY[y].sort(
        (a, b) => (a.x_position ?? 0) - (b.x_position ?? 0)
          || (a.display_order ?? 0) - (b.display_order ?? 0),
      ),
    }));
};

export const inferPresetIdFromRow = (rowWidgets) => {
  const first = rowWidgets?.[0];
  const config = parseWidgetConfig(first);
  return config?.preset_id || null;
};

export const inferRowIdFromRow = (rowWidgets) => {
  const first = rowWidgets?.[0];
  const config = parseWidgetConfig(first);
  return config?.row_id || null;
};

export const dashboardHasAnyReport = (widgets) =>
  (widgets || []).some((widget) => Boolean(parseWidgetConfig(widget)?.report_id));

export const inferRowTemplateFromPreset = (rowWidgets, preset) => {
  if (!preset?.rows?.length) {
    return null;
  }

  const rowId = inferRowIdFromRow(rowWidgets);
  if (rowId) {
    const byId = preset.rows.find((row) => row.id === rowId);
    if (byId) {
      return byId;
    }
  }

  const slotKeys = new Set(
    (rowWidgets || [])
      .map((widget) => parseWidgetConfig(widget)?.slot_key)
      .filter(Boolean),
  );

  if (slotKeys.size) {
    const bySlots = preset.rows.find((row) =>
      (row.cols || []).some((col) => slotKeys.has(col.slotKey || col.id)),
    );
    if (bySlots) {
      return bySlots;
    }
  }

  const slotTypes = new Set(
    (rowWidgets || [])
      .map((widget) => parseWidgetConfig(widget)?.slot_type)
      .filter(Boolean),
  );

  if (slotTypes.size === 1) {
    const slotType = [...slotTypes][0];
    const byType = preset.rows.find((row) =>
      row.rowType === slotType
      || (row.cols || []).some((col) => col.slotType === slotType),
    );
    if (byType) {
      return byType;
    }
  }

  return preset.rows.length === 1 ? preset.rows[0] : null;
};

export const inferDashboardModeFromWidgets = (widgets) => {
  const normalized = (widgets || []).map(normalizeWidget);

  if (!normalized.length) {
    return { dashboardMode: null, dashboardTemplateId: null };
  }

  const presetIds = new Set(
    normalized
      .map((w) => parseWidgetConfig(w)?.preset_id)
      .filter(Boolean),
  );

  if (
    presetIds.size === 1
    && presetIds.has(STANDARD_DASHBOARD_TEMPLATE_ID)
  ) {
    return {
      dashboardMode: 'template',
      dashboardTemplateId: STANDARD_DASHBOARD_TEMPLATE_ID,
    };
  }

  return { dashboardMode: 'custom', dashboardTemplateId: null };
};

/** Resolve which dashboard widget a report should attach to (update, never create duplicate row). */
export const resolveDashboardAttachTarget = (dashboardWidgets, {
  targetWidgetId,
  slotContext = {},
} = {}) => {
  const slot = slotContext || {};
  const resolvedTargetWidgetId = targetWidgetId ?? slot.targetWidgetId ?? null;
  const existingWidget = resolveDashboardTargetWidget(dashboardWidgets, {
    targetWidgetId: resolvedTargetWidgetId,
    slotKey: slot.slot_key,
    slotIndex: slot.slot_index,
    yPosition: slot.y_position,
    rowId: slot.row_id,
  });

  return {
    targetWidgetId: resolvedTargetWidgetId,
    existingWidget,
    widgetId: existingWidget?.id ?? resolvedTargetWidgetId ?? null,
    yPosition: existingWidget?.y_position
      ?? slot.y_position
      ?? getNextYPosition(dashboardWidgets),
    // attachToSlot: Boolean(
    //   resolvedTargetWidgetId
    //   || slot.slot_key != null
    //   || slot.slot_index != null,
    // ),
    attachToSlot: Boolean(existingWidget),
  };
};
